import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CreateIcon from '@mui/icons-material/Create';
import GetAppIcon from '@mui/icons-material/GetApp';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import {
  getSecurityEndpointDetailsHref,
  getSecurityFimEventDetailHref,
  getSecurityNavItemPath,
  SECURITY_ENDPOINTS_HREF,
} from 'src/mindtime/config/securityNav';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhFimEndpointItem,
  WazuhFimEventItem,
} from 'src/types/mindtime/wazuhFileMonitoring';
import type { Column, TableData } from 'src/types/table';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrGlobalScopeFilters,
  EdrPageHeader,
  SubscriptionUnavailableMessage,
  type EdrToolbarFilter,
  formatEdrNumber,
}  from '../shared/edrPrimitives';
import {
  buildFimUserFilterOptions,
  FIM_CHANGE_TYPE_FILTER_OPTIONS,
  FIM_RULE_LEVEL_FILTER_OPTIONS,
  FIM_SEVERITY_FILTER_OPTIONS,
  FimSummaryKpiGrid,
  getFimAffectedEndpointListColumns,
  getFimEventId,
  getFimEventListColumns,
} from './fileMonitoringDetailModel';
import { useFileMonitoringData, useFileMonitoringExport } from './useFileMonitoringData';

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    {
      name: 'File Integrity Monitoring',
      to: getSecurityNavItemPath('file-integrity-monitoring') ?? '',
    },
  ],
};


const FileIntegrityMonitoring: FC = () => {
  const navigate = useNavigate();
  const { setDateRange, setSelectedEndpointId, selectedClientId } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.fileIntegrityMonitoring);
  const {
    changeType = '',
    severityBand = '',
    ruleLevel = '',
    user = '',
  } = filters;
  const organizationId = selectedClientId ?? '';
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { canWidget, permissionsResolved } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'file-integrity-monitoring');
  const canSummary = canWidget('summary');
  const canEvents = canWidget('events');
  const canAffectedEndpoints = canWidget('affectedEndpoints');
  const showUnavailable =
    permissionsResolved && !canSummary && !canEvents && !canAffectedEndpoints;

  const fileFilters = useMemo(
    () => ({
      changeType,
      severityBand,
      ruleLevel,
      organizationId,
      user,
    }),
    [changeType, severityBand, ruleLevel, organizationId, user]
  );

  const openFimEventDetail = useCallback(
    (item: WazuhFimEventItem) => {
      const id = getFimEventId(item);
      if (!id) return;
      navigate(getSecurityFimEventDetailHref(id), {
        state: { record: item },
      });
    },
    [navigate]
  );

  const openEndpointDetails = useCallback(
    (item: WazuhFimEndpointItem) => {
      const systemWazuhAgentId = item.systemWazuhAgentId?.trim();
      if (!systemWazuhAgentId) return;
      navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId), {
        state: { endpointName: item.endpoint ?? item.name },
      });
    },
    [navigate]
  );

  const { loading, summary, items: paged, endpoints: endpointsPaged } = useFileMonitoringData(
    page,
    rowsPerPage,
    search,
    fileFilters
  );
  const { exporting, handleExport } = useFileMonitoringExport(search, fileFilters);

  const items = paged?.items ?? [];
  const totalRecords = paged?.totalRecords ?? 0;
  const affectedEndpoints = endpointsPaged?.items ?? [];
  const affectedEndpointsTotal = endpointsPaged?.totalRecords ?? 0;

  const userOptions = useMemo(
    () => buildFimUserFilterOptions(items, user),
    [items, user]
  );

  const handleResetFilters = useSecurityEdrToolbarReset(
    resetFilters,
    setDateRange,
    setPage,
    setSelectedEndpointId
  );

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'change',
        label: 'Change Type',
        icon: <CreateIcon style={{ fontSize: 14 }} />,
        options: FIM_CHANGE_TYPE_FILTER_OPTIONS,
        value: changeType,
        multiple: true,
        onChange: (value) => {
          setFilter('changeType', value);
          setPage(0);
        },
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: FIM_SEVERITY_FILTER_OPTIONS,
        value: severityBand,
        multiple: true,
        onChange: (value) => {
          setFilter('severityBand', value);
          setPage(0);
        },
      },
      {
        id: 'ruleLevel',
        label: 'Rule Level',
        options: FIM_RULE_LEVEL_FILTER_OPTIONS,
        value: ruleLevel,
        multiple: true,
        onChange: (value) => {
          setFilter('ruleLevel', value);
          setPage(0);
        },
      },
      {
        id: 'user',
        label: 'User',
        icon: <PersonIcon style={{ fontSize: 14 }} />,
        options: userOptions,
        value: user,
        multiple: true,
        onChange: (value) => {
          setFilter('user', value);
          setPage(0);
        },
      },
    ],
    [
      changeType,
      severityBand,
      ruleLevel,
      user,
      userOptions,
      setFilter,
    ]
  );

  const columns: Column[] = useMemo(() => getFimEventListColumns('global'), []);
  const affectedEndpointColumns: Column[] = useMemo(
    () => getFimAffectedEndpointListColumns(),
    []
  );

  const affectedEndpointsToolbar = (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
      <Typography style={{ fontSize: 13, fontWeight: 700, color: EDR_COLORS.textPrimary }}>
        Affected Endpoints
      </Typography>
      <Typography style={{ fontSize: 12, color: EDR_COLORS.textMuted }}>
        {formatEdrNumber(affectedEndpointsTotal)} endpoints affected
      </Typography>
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — File Monitoring')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="File Monitoring"
          subtitle="Track unauthorized file changes, deletions, and modifications."
        />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ gap: 12 }}
        >
          <EdrGlobalScopeFilters
            pageOrganizationId={organizationId || undefined}
            onChange={() => setPage(0)}
          />
          {canEvents ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export
            </Button>
          ) : null}
        </Box>
        {showUnavailable ? (
          <SubscriptionUnavailableMessage messageKey="unavailable.fileIntegrityMonitoring" />
        ) : null}
        {/* Create Monitoring Rule - no backend API yet */}

        {canSummary ? <FimSummaryKpiGrid summary={summary} /> : null}

        {canAffectedEndpoints ? (
          <EdrDataCard toolbar={affectedEndpointsToolbar}>
            <Table
              columns={affectedEndpointColumns}
              data={affectedEndpoints as unknown as TableData[]}
              loading={loading}
              showPagination={false}
              onRowClick={(row) => openEndpointDetails(row as unknown as WazuhFimEndpointItem)}
              TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
            />
          </EdrDataCard>
        ) : null}

        {canEvents ? (
        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={searchInput}
              onSearchChange={(v) => {
                setSearchInput(v);
                setPage(0);
              }}
              searchPlaceholder="Search Files…"
              filters={toolbarFilters}
              onReset={handleResetFilters}
            />
          )}
        >
          <Table
            columns={columns}
            data={items as unknown as TableData[]}
            loading={loading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            onRowClick={(row) => openFimEventDetail(row as unknown as WazuhFimEventItem)}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
        ) : null}
      </Box>
    </>
  );
};

export default FileIntegrityMonitoring;

